/**
 * INP² Auth Context
 *
 * Backend-enforced auth: state is hydrated from /api/auth/me using the JWT
 * stored in localStorage. There is no client-side user store anymore.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import {
  fetchCurrentUser,
  login as serviceLogin,
  logout as serviceLogout,
  getJwt,
  isJwtExpired,
} from './authService';

const AuthContext = createContext(null);

const initialState = {
  status: 'loading', // 'loading' | 'unauthenticated' | 'authenticated'
  user: null,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'UNAUTHENTICATED':
      return { ...state, status: 'unauthenticated', user: null, error: action.error || null };
    case 'AUTHENTICATED':
      return { ...state, status: 'authenticated', user: action.user, error: null };
    case 'AUTH_ERROR':
      return { ...state, error: action.error };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // On mount: if a JWT is present and unexpired, hydrate the user from /api/auth/me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getJwt();
      if (!token || isJwtExpired(token)) {
        dispatch({ type: 'UNAUTHENTICATED' });
        return;
      }
      const user = await fetchCurrentUser();
      if (cancelled) return;
      if (user) dispatch({ type: 'AUTHENTICATED', user });
      else dispatch({ type: 'UNAUTHENTICATED' });
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (usernameOrEmail, password) => {
    dispatch({ type: 'CLEAR_ERROR' });
    const result = await serviceLogin(usernameOrEmail, password);
    if (result.ok) {
      // /api/auth/login returns the user object directly; trust it.
      dispatch({ type: 'AUTHENTICATED', user: result.user });
      return { ok: true };
    }
    dispatch({ type: 'AUTH_ERROR', error: result.error });
    return { ok: false, error: result.error, locked: result.locked };
  }, []);

  const logout = useCallback(async () => {
    await serviceLogout();
    dispatch({ type: 'UNAUTHENTICATED' });
  }, []);

  const refresh = useCallback(async () => {
    const user = await fetchCurrentUser();
    if (user) dispatch({ type: 'AUTHENTICATED', user });
    else dispatch({ type: 'UNAUTHENTICATED' });
  }, []);

  const value = {
    user: state.user,
    isLoading: state.status === 'loading',
    isAuthenticated: state.status === 'authenticated',
    isSuperAdmin: state.user?.role === 'super_admin',
    isCustomer: state.user?.role === 'customer',
    isDemo: state.user?.role === 'demo',
    error: state.error,
    login,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
